import React from 'react';

import { IS_SERVER } from '@/constants/env';
import { useClientUrl } from '@/core/features/app/app-atoms';
import { useDeviceoutput } from '@/core/features/cookies/use-device-output';
import { devLogger } from '@/core/features/logger/logger';
import { getHeaderContainer, getPageContainer } from '@/core/features/page/page-element-serivce';
import { useRouterEvents } from '@/core/features/router/router-events';
import { atom, useAtom } from '@/core/features/store/atom-store';
import { WIREFRAME_HEADER_HEIGHT, WIREFRAME_SEARCH_OPEN_CLASS_MOBILE } from '@/core/features/wireframe/constants';
import { classC24mSearchIcon } from '@/core/features/wireframe/generated/wireframe-html-selectors-mobile';
import { getUrlPath } from '@/core/utils/url';

const INIT_TIMEOUT = 1200;

const LOGS_ENABLED = false;
const logEvent =
    IS_SERVER || !LOGS_ENABLED
        ? () => {}
        : // eslint-disable-next-line no-console
          (...message: (number | object | string)[]) =>
              devLogger.info(
                  `Auto Hide Wireframe Event --> ${message
                      .map((msg) => (typeof msg === 'object' ? JSON.stringify(msg) : msg))
                      .join(' ')}`,
              );

const hasExploitHeader = (): boolean => !!document.querySelector('#exploit-header');

const wasHeaderHiddenAtom = atom(false);

export default function useAutoHideWireframe(isBottomSheetInitialOpen: boolean = false) {
    const isInitializedRef = React.useRef(false);
    const wasHeaderEnabledRef = React.useRef<boolean | null>(null);
    const resetHeaderTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const { isApp } = useDeviceoutput();

    const routerEvents = useRouterEvents();

    const hasTouchedRef = React.useRef(false);

    const [clientUrl] = useClientUrl();
    const clientUrlRef = React.useRef(clientUrl);
    const hasClientUrlPathChanged = getUrlPath(clientUrlRef.current) !== getUrlPath(clientUrl);

    const [isStickyResetTimeoutEnabled, setStickyResetTimeoutEnabled] = React.useState(false);
    const stickyResetTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const currentWireframeHeaderHeight = isApp ? 0 : WIREFRAME_HEADER_HEIGHT;

    const [wasHeaderHidden, setWasHeaderHidden] = useAtom(wasHeaderHiddenAtom);

    const isHeaderFixed = () => {
        const header = getHeaderContainer();
        return header?.classList.contains('fixed');
    };

    const isHeaderSticky = () => {
        const header = getHeaderContainer();
        return header?.classList.contains('sticky');
    };

    const getScrollContainer = React.useCallback(() => {
        if (IS_SERVER) {
            return null;
        }
        const pageContainer = getPageContainer();
        if (pageContainer && pageContainer.classList.contains('overflow-y-scroll')) {
            return pageContainer;
        }
        return window;
    }, []);

    const getScrollTop = React.useCallback(() => {
        if (IS_SERVER) {
            return 0;
        }
        const scrollContainer = getScrollContainer();

        if (!scrollContainer) {
            return 0;
        }

        if (scrollContainer === window) {
            return window.scrollY;
        }
        if (scrollContainer instanceof HTMLElement) {
            return scrollContainer.scrollTop;
        }
        return 0;
    }, [getScrollContainer]);

    const clearResetHeaderTimeout = React.useCallback(() => {
        if (resetHeaderTimeoutRef.current === null) {
            return;
        }
        clearTimeout(resetHeaderTimeoutRef.current);
        resetHeaderTimeoutRef.current = null;
    }, []);

    const resetHeader = React.useCallback(
        ({ debugNamespace }: { debugNamespace: string }) => {
            const header = getHeaderContainer();
            const page = getHeaderContainer();
            if (!header || !page) {
                return;
            }
            logEvent('resetHeader, from:', debugNamespace);
            clearResetHeaderTimeout();

            wasHeaderEnabledRef.current = null;

            header.classList.remove('fixed');
            header.classList.remove('sticky');
            header.classList.remove('top-0');
        },
        [clearResetHeaderTimeout],
    );

    const setHeaderFixed = React.useCallback(() => {
        const header = getHeaderContainer();
        const page = getScrollContainer();

        if (!header || !page) {
            return;
        }

        if (isHeaderFixed()) {
            return;
        }

        resetHeader({ debugNamespace: 'setHeaderFixed' });

        logEvent('setHeaderFixed');
        header.classList.add('fixed', 'width-100', 'top-0');
        setWasHeaderHidden(true);
    }, [getScrollContainer, resetHeader, setWasHeaderHidden]);

    const hideHeader = React.useCallback(
        ({ debugNamespace }: { debugNamespace: string }) => {
            if (isHeaderFixed()) {
                return;
            }
            logEvent('hideHeader, from:', debugNamespace);
            clearResetHeaderTimeout();

            const pageContainer = getPageContainer();

            if (!pageContainer) {
                return;
            }

            const isSearchOpen = handleSearchOpen(hideHeader);
            if (isSearchOpen) {
                return;
            }

            const shouldScrollPage = pageContainer.scrollTop < WIREFRAME_HEADER_HEIGHT;
            const shouldScrollWindow = window.scrollY < WIREFRAME_HEADER_HEIGHT;
            if (shouldScrollPage) {
                pageContainer.scrollTo({ behavior: 'smooth', top: WIREFRAME_HEADER_HEIGHT });
            }
            if (shouldScrollWindow) {
                window.scrollTo({ behavior: 'smooth', top: WIREFRAME_HEADER_HEIGHT });
            }
            setTimeout(() => {
                setHeaderFixed();
                if (shouldScrollPage) {
                    pageContainer.scrollTo({ behavior: 'instant', top: 0 });
                }
                if (shouldScrollWindow) {
                    window.scrollTo({ behavior: 'instant', top: -WIREFRAME_HEADER_HEIGHT });
                }
            }, 400);
        },
        [clearResetHeaderTimeout, setHeaderFixed],
    );

    const setHeaderSticky = React.useCallback(() => {
        const header = getHeaderContainer();
        const scrollContainer = getScrollContainer();
        if (!header || !scrollContainer) {
            return;
        }

        if (isHeaderSticky()) {
            return;
        }

        const scrollTop = getScrollTop();

        if (scrollTop > currentWireframeHeaderHeight) {
            return;
        }

        resetHeader({ debugNamespace: 'setHeaderSticky' });

        logEvent('setHeaderSticky');
        header.classList.add('sticky', 'width-100', 'top-0');
        setStickyResetTimeoutEnabled(true);

        if (isInitializedRef.current) {
            logEvent('setHeaderSticky scroll');
            scrollContainer.scrollTo({ behavior: 'instant', top: currentWireframeHeaderHeight });
        }
    }, [currentWireframeHeaderHeight, getScrollContainer, getScrollTop, resetHeader]);

    // If the bottom sheet is initially open (half or fully), then the header must be fixed
    React.useEffect(() => {
        if (isBottomSheetInitialOpen) {
            setHeaderFixed();
        }
    }, [isBottomSheetInitialOpen, setHeaderFixed]);

    /**
     * Effect: Hide header after 3 seconds after it was enabled
     */
    React.useEffect(() => {
        if (!isStickyResetTimeoutEnabled) {
            return;
        }

        stickyResetTimeoutRef.current = setTimeout(() => {
            hideHeader({ debugNamespace: 'hideHeaderAfter3Seconds' });
            setStickyResetTimeoutEnabled(false);
        }, 3000);

        return () => {
            if (stickyResetTimeoutRef.current !== null) {
                clearTimeout(stickyResetTimeoutRef.current);
            }
        };
    }, [isStickyResetTimeoutEnabled, hideHeader]);

    /**
     * Effect: on page load auto hide the header by scrolling
     */
    React.useEffect(() => {
        if (isInitializedRef.current) {
            return;
        }

        if (!isHeaderFixed() && !isHeaderSticky()) {
            if (wasHeaderHidden) {
                setHeaderFixed();
            } else {
                setHeaderSticky();
            }
        }
        const loadTimeout = setTimeout(() => {
            if (isHeaderFixed()) {
                // browser back
                return;
            }

            // Regression: fallback for browsers that do not handle browser back correctly, e.g. android browser and android firefox
            if (!isHeaderFixed() && !isHeaderSticky()) {
                setHeaderFixed();
            }
        }, INIT_TIMEOUT); // show the header to the user shortly

        return () => {
            clearTimeout(loadTimeout);
        };
    }, [hideHeader, setHeaderFixed, setHeaderSticky, wasHeaderHidden]);

    /**
     * Effect: Initialize app, has to run separately in order to work properly on browser back
     */
    React.useEffect(() => {
        const initTimeout = setTimeout(() => {
            logEvent('init');

            isInitializedRef.current = true;
        }, INIT_TIMEOUT);

        return () => {
            clearTimeout(initTimeout);
        };
    }, []);

    /**
     * Effect: Remove overscroll from body to prevent rubber band effect
     */
    React.useEffect(() => {
        const body = document.querySelector('body');

        if (body?.classList.contains('overscroll-none')) {
            return;
        }

        body?.classList.add('overscroll-none');

        return () => {
            body?.classList.remove('overscroll-none');
        };
    }, []);

    /**
     * Effect: Set hasTouchedRef to true on touchstart or wheel event
     */
    React.useEffect(() => {
        const touchHandler = () => {
            hasTouchedRef.current = true;
        };
        document.addEventListener('touchstart', touchHandler);
        document.addEventListener('wheel', touchHandler);

        return () => {
            document.removeEventListener('touchstart', touchHandler);
            document.removeEventListener('wheel', touchHandler);
        };
    }, []);

    /**
     * Effect: set header fixed on page transition
     */
    React.useEffect(() => {
        const handler = () => {
            setHeaderFixed();
        };
        const timeout = setTimeout(() => {
            routerEvents.on('routeChangeStart', handler);
        }, 100); // first visit it considered route change, skip it

        return () => {
            clearTimeout(timeout);
            routerEvents.off('routeChangeStart', handler);
        };
    }, [routerEvents, setHeaderFixed]);

    /**
     * Effect: Cleanup
     */
    React.useEffect(() => {
        if (!hasClientUrlPathChanged) {
            return;
        }

        const routeChangeCompleteHandler = () => {
            setTimeout(() => {
                if (hasExploitHeader()) {
                    return;
                }

                resetHeader({ debugNamespace: 'routeChangeComplete' });
            }, 200);
            routerEvents.off('routeChangeComplete', routeChangeCompleteHandler);
        };

        routerEvents.on('routeChangeComplete', routeChangeCompleteHandler);
    }, [hasClientUrlPathChanged, resetHeader, routerEvents]);

    return { setHeaderSticky };
}

const handleSearchOpen = (hideHeader: ({ debugNamespace }: { debugNamespace: string }) => void) => {
    const searchOpen = document.querySelector(`.${WIREFRAME_SEARCH_OPEN_CLASS_MOBILE}`);
    const searchIcon = document.querySelector(`.${classC24mSearchIcon}`);
    const page = getPageContainer();

    if (!page || !searchOpen || !(searchIcon instanceof HTMLElement)) {
        return false;
    }

    // hide header when search is closed
    searchIcon.addEventListener('click', () => hideHeader({ debugNamespace: 'searchIconClick' }));

    const toggleSearch = () => {
        if (!document.querySelector(`.${WIREFRAME_SEARCH_OPEN_CLASS_MOBILE}`)) {
            return;
        }

        searchIcon.click();
    };

    // hide search when user scrolls
    page.addEventListener('touchmove', toggleSearch);
    window.addEventListener('touchmove', toggleSearch);

    return true;
};
